import { apiService } from "@/core/api";
import { API_ENDPOINTS } from "@/core/constants/endpoints";
import React, { useCallback, useEffect, useState } from "react";
import "./SubscriptionTab.scss";
import { SvgIcon } from "@/core/components/svgIcon/SvgIcon";
import {
  CheckoutLinkResponse,
  ActiveSubscriptionResponse,
} from "@/features/settings/service/userService";
import { $userBasicInfo } from "@/features/user/store";
import { useUnit } from "effector-react";
import { CommonModal } from "@/core/components/commonModal/CommonModal";
import { Button } from "primereact/button";
import { Plan, PlansResponse } from "@/features/accountSetup";

export const SubscriptionTab: React.FC<{ status?: "success" | "fail" }> = ({
  status,
}) => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [plansData, setPlansData] = useState<PlansResponse | null>(null);
  const [plansLoading, setPlansLoading] = useState<boolean>(false);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [allowToSelect, setAllowToSelect] = useState<boolean>(false);
  const [userBasicInfo] = useUnit([$userBasicInfo]);
  const [billedAnually, setBilledAnually] = useState<boolean>(false);
  const [activeSubscription, setActiveSubscription] =
    useState<ActiveSubscriptionResponse | null>(null);
  const [activeSubscriptionLoading, setActiveSubscriptionLoading] =
    useState<boolean>(false);

  // Modal states
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showContactAdminModal, setShowContactAdminModal] = useState(false);
  const [showCreateOrgModal, setShowCreateOrgModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);
  const [showCancelSuccessModal, setShowCancelSuccessModal] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  const fetchPlans = useCallback(async () => {
    setPlansLoading(true);
    try {
      const response = await apiService.get<PlansResponse>(
        API_ENDPOINTS.SUBSCRIPTIONS.GET_PLANS,
      );
      setPlansData(response);
      setPlans(response.data);
      // console.log("Plans fetched:", response.data);
    } catch (error) {
      console.error("Failed to fetch plans:", error);
      setPlans([]);
    } finally {
      setPlansLoading(false);
    }
  }, []);

  const fetchActiveSubscription = useCallback(async () => {
    setActiveSubscriptionLoading(true);
    try {
      const response = await apiService.get<ActiveSubscriptionResponse>(
        API_ENDPOINTS.SUBSCRIPTIONS.GET_ACTIVE_SUBSCRIPTION,
      );
      setActiveSubscription(response);
      // console.log("Active subscription fetched:", response);
    } catch (error) {
      console.error("Failed to fetch active subscription:", error);
      setActiveSubscription(null);
    } finally {
      setActiveSubscriptionLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
    fetchActiveSubscription();
  }, [fetchPlans, fetchActiveSubscription]);

  useEffect(() => {
    if (userBasicInfo?.partner && typeof userBasicInfo.partner === "object") {
      setAllowToSelect(userBasicInfo.partner.status === "admin");
    }
  }, [userBasicInfo]);

  // Auto-toggle billing period based on current plan
  useEffect(() => {
    if (plans?.length > 0) {
      const currentPlan = plans.find((plan) => plan.current_plan === true);
      if (currentPlan) {
        // If current plan is yearly, set billedAnually to true
        setBilledAnually(currentPlan.interval === "year");
      }
    }
  }, [plans]);

  const getcheckoutLink = async (price_id: string) => {
    setCheckoutLoading(price_id);

    try {
      const response = await apiService.post<CheckoutLinkResponse>(
        API_ENDPOINTS.SUBSCRIPTIONS.GET_STRIPE_LINK,
        {
          price_id,
        },
      );

      // console.log("Checkout link response:", response);

      if (response.session_url) {
        window.location.href = response.session_url;
      } else {
        console.error("No checkout URL in response");
        setErrorMessage("Failed to get checkout link. Please try again.");
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error("Failed to get checkout link:", error);
      setErrorMessage("Failed to initiate checkout. Please try again.");
      setShowErrorModal(true);
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handlePlanClick = (plan: Plan) => {
    if (checkoutLoading) return;

    if (!allowToSelect && !plan.current_plan) {
      if (plansData?.org_role === "member") {
        setShowContactAdminModal(true);
      } else if (plansData?.org_role === "Freelancer") {
        setShowCreateOrgModal(true);
      }
      return;
    }

    if (allowToSelect && !plan.current_plan) {
      setSelectedPlan(plan);
      setShowConfirmModal(true);
    }
  };

  const handleConfirmPayment = () => {
    if (selectedPlan) {
      setShowConfirmModal(false);
      getcheckoutLink(selectedPlan.price_id);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelLoading(true);
    try {
      await apiService.post(
        API_ENDPOINTS.SUBSCRIPTIONS.CANCEL,
        {},
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      // console.log("Subscription cancelled successfully");
      setShowCancelConfirmModal(false);
      // Reload plans to reflect the change
      await fetchPlans();
      // Optionally reload user info to update the UI
      window.location.reload();
    } catch (error) {
      console.error("Failed to cancel subscription:", error);
      setErrorMessage("Failed to cancel subscription. Please try again.");
      setShowErrorModal(true);
      setShowCancelConfirmModal(false);
    } finally {
      setCancelLoading(false);
    }
  };

  const hasActivePaidPlan = () => {
    return plans.some((plan) => plan.current_plan === true);
  };

  const getPlanRank = (planName: string | undefined | null): number => {
    if (!planName) return 0;
    const name = planName.toLowerCase();
    if (name.includes("premium")) return 2;
    if (name.includes("professional")) return 1;
    return 0; // Starter
  };

  const getCurrentPlanName = (): string | null => {
    const plan = userBasicInfo?.plan;
    if (!plan || typeof plan === "boolean") return null;
    if ("plan_name" in plan) return plan.plan_name;
    if ("subscription" in plan) return plan.subscription?.plan ?? null;
    return null;
  };

  const getPlanAction = (
    plan?: Plan | null,
  ): "upgrade" | "downgrade" | "equal" => {
    const currentPlanName = getCurrentPlanName();
    const currentRank = getPlanRank(currentPlanName);
    const targetPlan = plan || selectedPlan;
    const targetRank = getPlanRank(targetPlan?.product_name);

    if (targetRank > currentRank) return "upgrade";
    if (targetRank < currentRank) return "downgrade";
    return "equal";
  };

  return (
    <div className="settings-content subscription-tab">
      {status === "success" && (
        <div className="subscription-status success">
          <p>
            Success! Your EUcalls subscription plan has been activated and full
            access is now available.
          </p>
        </div>
      )}
      {status === "fail" && (
        <div className="subscription-status fail">
          <p>
            Something went wrong while activating your EUcalls subscription
            plan. No charges were made. Please try again.
          </p>
        </div>
      )}
      <div className="section-header">
        <div className="section-header-text">
          <h2>Your subscription plan</h2>
          <p>Adjust your subscription anytime.</p>
        </div>
        <div className="billing-toggle">
          <span className={`toggle-label ${!billedAnually ? "active" : ""}`}>
            Billed monthly
          </span>
          <div
            className={`toggle-switch ${billedAnually ? "active" : ""}`}
            onClick={() => setBilledAnually(!billedAnually)}
          >
            <div className="toggle-slider"></div>
          </div>
          <span className={`toggle-label ${billedAnually ? "active" : ""}`}>
            Billed annually
            <span className={`discount-badge ${billedAnually ? "on" : "off"}`}>
              20% off
            </span>
          </span>
        </div>
      </div>

      {/* Active Subscription Card */}
      {activeSubscriptionLoading && (
        <div className="active-subscription-card loading">
          <div className="loading-state">Loading subscription details...</div>
        </div>
      )}
      {!activeSubscriptionLoading &&
        activeSubscription &&
        typeof activeSubscription !== "boolean" &&
        activeSubscription.subscription !== null && (
          <div className="active-subscription-card">
            <div className="subscription-header">
              <h3>Current Subscription</h3>
            </div>
            <div className="subscription-details">
              <div className="subscription-detail-item">
                <span className="detail-label">Plan:</span>
                <span className="detail-value">
                  {activeSubscription.subscription.plan}
                </span>
              </div>
              <div className="subscription-detail-item">
                <span className="detail-label">Status:</span>
                <span
                  className={`detail-value status-${activeSubscription.subscription.status.toLowerCase()}`}
                >
                  {activeSubscription.subscription.status}
                </span>
              </div>
              <div className="subscription-detail-item">
                <span className="detail-label">Period:</span>
                <span className="detail-value">
                  {new Date(
                    activeSubscription.subscription.period.start,
                  ).toLocaleDateString()}{" "}
                  -{" "}
                  {new Date(
                    activeSubscription.subscription.period.end,
                  ).toLocaleDateString()}
                </span>
              </div>
              <div className="subscription-detail-item">
                <span className="detail-label">
                  {activeSubscription.subscription.canceled
                    ? "Expires on:"
                    : "Auto-renews on:"}
                </span>
                <span className="detail-value">
                  {new Date(
                    activeSubscription.subscription.billing_date,
                  ).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        )}

      {/* Available Plans Section */}
      <div className="available-plans">
        {plansLoading && <div className="loading-state">Loading plans...</div>}
        {!plansLoading && plans && plans.length > 0 && (
          <div className="plan-grid">
            {/* Starter Plan */}
            <div
              className={`plan-card ${
                plans.filter((e) => e.current_plan === true).length === 0
                  ? "current"
                  : ""
              }`}
              style={{ cursor: "default" }}
            >
              <div className="plan-header">
                <div className="plan-icon">
                  <SvgIcon icon="icon-starter" />
                </div>
                <h3 className="plan-name">Starter Plan</h3>
              </div>

              <div className="plan-body-content">
                <div className="plan-pricing">
                  <span className="price">0€</span>
                  <span className="price-period">per month</span>
                </div>

                <div className="plan-features">
                  <ul>
                    <li>Your organisation profile</li>
                    <li>Unlimited access to EU calls</li>
                    <li>Access to list of partners</li>
                    <li>Contact new partners</li>
                  </ul>
                </div>
              </div>

              <div className="plan-footer">
                {plans.filter((plan) => plan.current_plan === true).length ===
                0 ? (
                  <div className="current-plan-badge">Current plan</div>
                ) : (
                  <button
                    className="upgrade-button"
                    onClick={() => {
                      if (!allowToSelect) {
                        if (plansData?.org_role === "member") {
                          setShowContactAdminModal(true);
                        } else if (plansData?.org_role === "Freelancer") {
                          setShowCreateOrgModal(true);
                        }
                      } else {
                        setShowCancelConfirmModal(true);
                      }
                    }}
                    disabled={cancelLoading}
                    style={{ cursor: cancelLoading ? "wait" : "pointer" }}
                  >
                    {cancelLoading ? "Processing..." : "Downgrade to this plan"}
                  </button>
                )}
              </div>
            </div>

            {/* Paid Plans */}
            {plans
              .filter((plan) =>
                billedAnually
                  ? plan.interval === "year"
                  : plan.interval === "month",
              )
              .map((plan) => {
                const isCheckoutLoading = checkoutLoading === plan.price_id;
                const isAnyCheckoutLoading = checkoutLoading !== null;
                const isProfessional = plan.product_name
                  .toLowerCase()
                  .includes("professional");
                const isPremium = plan.product_name
                  .toLowerCase()
                  .includes("premium");

                return (
                  <div
                    key={plan.price_id}
                    className={`plan-card ${
                      plan.current_plan ? "current" : ""
                    } ${isCheckoutLoading ? "loading" : ""} ${
                      isAnyCheckoutLoading && !isCheckoutLoading
                        ? "disabled"
                        : ""
                    }`}
                    onClick={() => handlePlanClick(plan)}
                    style={{
                      cursor: isAnyCheckoutLoading
                        ? isCheckoutLoading
                          ? "wait"
                          : "not-allowed"
                        : plan.current_plan
                          ? "default"
                          : "pointer",
                      opacity:
                        isAnyCheckoutLoading && !isCheckoutLoading ? 0.5 : 1,
                    }}
                  >
                    <div className="plan-header">
                      <div className="plan-icon">
                        {isProfessional && <SvgIcon icon="icon-pro-plan" />}
                        {isPremium && <SvgIcon icon="icon-premium-plan" />}
                      </div>
                      <h3 className="plan-name">{plan.product_name}</h3>
                    </div>

                    <div className="plan-body-content">
                      <div className="plan-pricing">
                        <span className="price">
                          {plan.base_price}
                          {plan.currency === "EUR" && "€"}
                        </span>
                        <span className="price-period">per month</span>
                        {billedAnually && (
                          <div className="billing-info">
                            {(parseFloat(plan.base_price) * 12).toFixed(0)}€
                            billed annually
                          </div>
                        )}
                      </div>

                      <div className="plan-subtitle">
                        Everything in{" "}
                        {isProfessional ? "Starter" : "Professional"} plus:
                      </div>

                      <div
                        className="plan-features"
                        dangerouslySetInnerHTML={{ __html: plan.description }}
                      />
                    </div>

                    <div className="plan-footer">
                      {plan.current_plan ? (
                        <div className="current-plan-badge">Current plan</div>
                      ) : (
                        <>
                          {userBasicInfo?.free_trial &&
                          isProfessional &&
                          plan.has_free_trial ? (
                            <button className="upgrade-button free-trial">
                              {isCheckoutLoading
                                ? "Processing..."
                                : "Start your 14-day free trial"}
                            </button>
                          ) : (
                            <button className="upgrade-button">
                              {isCheckoutLoading
                                ? "Redirecting..."
                                : getPlanAction(plan) === "upgrade"
                                  ? "Upgrade to this plan"
                                  : getPlanAction(plan) === "downgrade"
                                    ? "Downgrade to this plan"
                                    : "Select this plan"}
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>

      {/* Error Modal */}
      <CommonModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        icon="icon-alert"
        width={400}
        height={250}
        actions={
          <div className="form-actions">
            <Button
              type="button"
              className="btn-primary"
              onClick={() => setShowErrorModal(false)}
            >
              OK
            </Button>
          </div>
        }
      >
        <div className="modal-content">
          <h3 className="modal-title">Error</h3>
          <p className="modal-message">{errorMessage}</p>
        </div>
      </CommonModal>

      {/* Contact Admin Modal */}
      <CommonModal
        isOpen={showContactAdminModal}
        onClose={() => setShowContactAdminModal(false)}
        icon=""
        width={450}
        height={330}
        actions={
          <div className="form-actions">
            <Button
              type="button"
              className="btn-secondary"
              onClick={() => setShowContactAdminModal(false)}
            >
              OK
            </Button>
          </div>
        }
      >
        <div className="modal-content">
          <h3 className="modal-title">Contact Organization Admin</h3>
          <p className="modal-message">
            You need administrator privileges to change the subscription plan.
            Please contact your organization admin to change plan.
          </p>
        </div>
      </CommonModal>

      {/* Create Organization Modal */}
      <CommonModal
        isOpen={showCreateOrgModal}
        onClose={() => setShowCreateOrgModal(false)}
        icon=""
        width={500}
        height={320}
        actions={
          <div className="form-actions">
            <Button
              type="button"
              className="btn-secondary"
              onClick={() => setShowCreateOrgModal(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="btn-primary"
              onClick={() => {
                window.location.href = "/settings/organization";
                setShowCreateOrgModal(false);
              }}
            >
              Create Organization
            </Button>
          </div>
        }
      >
        <div className="modal-content">
          <h3 className="modal-title">Create Organization Required</h3>
          <p className="modal-message">
            You need to create an organization to access premium plans. As a
            freelancer, you can upgrade by first creating an organization
            profile.
          </p>
        </div>
      </CommonModal>

      {/* Confirm Payment Modal */}
      <CommonModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        icon="icon-plan"
        width={550}
        height={320}
        actions={
          <div className="form-actions">
            <Button
              type="button"
              className="btn-secondary"
              onClick={() => setShowConfirmModal(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="btn-primary"
              onClick={handleConfirmPayment}
            >
              Go to payment
            </Button>
          </div>
        }
      >
        <div className="modal-content">
          <h3 className="modal-title plan-confirmation-title">
            {getPlanAction() === "upgrade" ? (
              <>
                Ready to upgrade to {selectedPlan?.product_name}?
                {/* {activeSubscription &&
                  typeof activeSubscription !== "boolean" &&
                  activeSubscription.subscription !== null && ( */}
                <>
                  <br />
                  <span className="danger-text">
                    Your current active plan will be canceled.
                  </span>
                </>
                {/* )} */}
              </>
            ) : getPlanAction() === "downgrade" ? (
              <>
                Are you sure you want to switch to our{" "}
                {selectedPlan?.product_name} plan?
                {activeSubscription &&
                  typeof activeSubscription !== "boolean" &&
                  activeSubscription.subscription !== null && (
                    <>
                      <br />
                      <span className="danger-text">
                        Your current active plan will be canceled.
                      </span>
                    </>
                  )}
              </>
            ) : (
              <>
                Ready to select {selectedPlan?.product_name}?
                {activeSubscription &&
                  typeof activeSubscription !== "boolean" &&
                  activeSubscription.subscription !== null && (
                    <>
                      <br />
                      <span className="danger-text">
                        Your current active plan will be canceled.
                      </span>
                    </>
                  )}
              </>
            )}
          </h3>
          <div className="modal-plan-details">
            <span className="plan-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  d="M1.33301 9.66638L7.76116 12.8805C7.84862 12.9242 7.89234 12.9461 7.93821 12.9547C7.97883 12.9623 8.02052 12.9623 8.06114 12.9547C8.10701 12.9461 8.15073 12.9242 8.23819 12.8805L14.6663 9.66638M1.33301 6.33305L7.76116 3.11897C7.84862 3.07525 7.89234 3.05338 7.93821 3.04478C7.97883 3.03716 8.02052 3.03716 8.06114 3.04478C8.10701 3.05338 8.15073 3.07525 8.23819 3.11897L14.6663 6.33305L8.23819 9.54713C8.15073 9.59085 8.10701 9.61272 8.06114 9.62132C8.02052 9.62895 7.97883 9.62895 7.93821 9.62132C7.89234 9.61272 7.84862 9.59085 7.76116 9.54713L1.33301 6.33305Z"
                  stroke="#414651"
                  stroke-width="1.33333"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </span>
            <span className="plan-name">
              <div className="plan-name-title">
                {selectedPlan?.product_name}
                <span>{selectedPlan?.base_price}€ per month</span>
              </div>
              <div className="plan-yearly">
                {parseInt(selectedPlan?.base_price!) * 12}€ per year{" "}
              </div>
            </span>
          </div>
          {/* <p className="modal-message">You will be redirected to complete your payment.</p> */}
        </div>
      </CommonModal>

      {/* Cancel Subscription Modal */}
      <CommonModal
        isOpen={showCancelConfirmModal}
        onClose={() => !cancelLoading && setShowCancelConfirmModal(false)}
        icon="icon-starter"
        width={500}
        height={300}
        actions={
          <div className="form-actions">
            <Button
              type="button"
              className="btn-secondary"
              onClick={() => setShowCancelConfirmModal(false)}
              disabled={cancelLoading}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="btn-primary"
              onClick={handleCancelSubscription}
              disabled={cancelLoading}
            >
              {cancelLoading ? "Processing..." : "Confirm"}
            </Button>
          </div>
        }
      >
        <div className="modal-content">
          <h3 className="modal-title">Cancel Subscription</h3>
          <p className="modal-message">
            Are you sure you want to switch to the Starter Plan? Your current
            subscription will be canceled immediately and you will lose access
            to premium features.
          </p>
        </div>
      </CommonModal>
    </div>
  );
};
